import React, { useState, useRef } from "react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  ResponsiveContainer,
} from "recharts";
import { CheckCircle2, AlertTriangle, Dumbbell, Target, Gauge, Ruler, Info, Download, Play, Pause, RotateCcw, Columns2, ChevronLeft, ChevronRight, Eye, EyeOff, Link2 } from "lucide-react";
import { scoreTier, ratingColor, mediaUrl } from "../lib/api";
import { generateReportPdf } from "../lib/pdf";
import { VideoOverlay } from "./VideoOverlay";
import { toast } from "sonner";

const ScoreRing = ({ score, size = 132 }) => {
  const tier = scoreTier(score);
  const r = size / 2 - 10;
  const c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#1E293B" strokeWidth="9" fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={tier.color}
          strokeWidth="9"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1s cubic-bezier(0.22,1,0.36,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono-tel font-bold text-4xl" style={{ color: tier.color }}>
          {score}
        </span>
        <span className="font-mono-tel text-[9px] tracking-[0.2em] text-slate-500 uppercase">/ 100</span>
      </div>
    </div>
  );
};

const PillarBar = ({ pillar, testId }) => {
  const tier = scoreTier(pillar.score);
  return (
    <div data-testid={testId} className="rounded-xl border border-slate-800 bg-[#12181F] p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-slate-200">{pillar.title}</span>
        <span className="font-mono-tel font-bold text-lg" style={{ color: tier.color }}>
          {pillar.score}
        </span>
      </div>
      <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-1000"
          style={{ width: `${pillar.score}%`, backgroundColor: tier.color }}
        />
      </div>
      <p className="text-xs text-slate-400 mt-2 leading-relaxed">{pillar.assessment}</p>
    </div>
  );
};

const PILLAR_IDS = {
  stance_alignment: "score-stance-alignment",
  movement_efficiency: "score-movement-efficiency",
  shot_execution: "score-shot-execution",
  mental_focus: "score-mental-focus",
};

const confBadge = (c) => {
  const v = (c || "").toLowerCase();
  if (v === "high") return { label: "HIGH CONFIDENCE", color: "#10B981" };
  if (v === "medium") return { label: "MEDIUM CONFIDENCE", color: "#F59E0B" };
  return { label: "LOW CONFIDENCE", color: "#FB923C" };
};

const BallMetrics = ({ metrics }) => {
  const m = metrics || {};
  const can = m.can_estimate && (m.shot_speed_kmh != null || m.shot_distance_m != null);

  if (!can) {
    return (
      <div
        data-testid="ball-metrics-insufficient"
        className="rounded-2xl border border-slate-800 bg-[#12181F] p-5 flex items-start gap-4"
      >
        <div className="h-10 w-10 rounded-xl bg-slate-800/70 border border-slate-700 flex items-center justify-center shrink-0">
          <Info className="h-5 w-5 text-slate-400" />
        </div>
        <div>
          <p className="font-mono-tel text-[10px] tracking-[0.2em] text-slate-500 uppercase mb-1">
            Ball Speed & Shot Distance
          </p>
          <p className="text-sm text-slate-300 leading-relaxed">
            Speed and distance assessment isn't available due to video quality — there isn't enough
            information in this clip to reliably estimate the speed (km/h) and distance (metres) of the shot.
          </p>
          {m.reason && <p className="text-xs text-slate-500 mt-1.5">{m.reason}</p>}
        </div>
      </div>
    );
  }

  const conf = confBadge(m.confidence);
  return (
    <div data-testid="ball-metrics-estimate" className="rounded-2xl border border-slate-800 bg-[#12181F] p-5">
      <div className="flex items-center justify-between mb-4">
        <p className="font-mono-tel text-[10px] tracking-[0.2em] text-cyan-400 uppercase">
          Ball Speed & Shot Distance
        </p>
        <span
          className="font-mono-tel text-[9px] tracking-wider uppercase px-2.5 py-1 rounded-full"
          style={{ color: conf.color, background: `${conf.color}1f`, border: `1px solid ${conf.color}55` }}
        >
          {conf.label}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-800 bg-[#0B0F12] p-4 flex items-center gap-4">
          <div className="h-11 w-11 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
            <Gauge className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <p className="font-mono-tel text-[10px] text-slate-500 uppercase tracking-wider">Est. Shot Speed</p>
            <p className="font-mono-tel font-bold text-2xl text-slate-100">
              {m.shot_speed_kmh != null ? (
                <>
                  {Math.round(m.shot_speed_kmh)}
                  <span className="text-sm text-slate-500 ml-1">km/h</span>
                </>
              ) : (
                <span className="text-sm text-slate-500">—</span>
              )}
            </p>
          </div>
        </div>
        <div className="rounded-xl border border-slate-800 bg-[#0B0F12] p-4 flex items-center gap-4">
          <div className="h-11 w-11 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
            <Ruler className="h-5 w-5 text-cyan-400" />
          </div>
          <div>
            <p className="font-mono-tel text-[10px] text-slate-500 uppercase tracking-wider">Est. Shot Distance</p>
            <p className="font-mono-tel font-bold text-2xl text-slate-100">
              {m.shot_distance_m != null ? (
                <>
                  {Math.round(m.shot_distance_m * 10) / 10}
                  <span className="text-sm text-slate-500 ml-1">m</span>
                </>
              ) : (
                <span className="text-sm text-slate-500">—</span>
              )}
            </p>
          </div>
        </div>
      </div>
      {m.assumptions && (
        <p className="text-xs text-slate-500 mt-3 flex items-start gap-1.5">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {m.assumptions}
        </p>
      )}
    </div>
  );
};

const SyncPlayer = ({ assessmentId, videos, showOverlay, setShowOverlay }) => {
  const refs = useRef([]);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const anyOverlay = (videos || []).some((v) => v.overlay && v.overlay.available);
  const forEachVid = (fn) => refs.current.forEach((v) => v && fn(v));

  const toggle = () => {
    if (playing) {
      forEachVid((v) => v.pause());
      setPlaying(false);
    } else {
      forEachVid((v) => {
        v.currentTime = refs.current[0]?.currentTime || 0;
        v.play().catch(() => {});
      });
      setPlaying(true);
    }
  };

  const restart = () => {
    forEachVid((v) => (v.currentTime = 0));
    setProgress(0);
  };

  const seek = (frac) => {
    const t = frac * (duration || 0);
    forEachVid((v) => (v.currentTime = t));
    setProgress(frac);
  };

  const FRAME = 1 / 30;
  const stepBy = (delta) => {
    forEachVid((v) => v.pause());
    setPlaying(false);
    const base = refs.current[0]?.currentTime || 0;
    const t = Math.max(0, Math.min(duration || base, base + delta));
    forEachVid((v) => (v.currentTime = t));
    if (duration) setProgress(t / duration);
  };

  return (
    <div data-testid="sync-player" className="flex flex-col gap-3">
      <div className={`grid gap-3 ${videos.length > 2 ? "grid-cols-2" : "grid-cols-1 sm:grid-cols-2"}`}>
        {videos.map((v, i) => (
          <div key={i} className="relative rounded-xl overflow-hidden border border-slate-800 bg-black">
            <video
              ref={(el) => (refs.current[i] = el)}
              data-testid={`sync-video-${i}`}
              src={mediaUrl(assessmentId, i)}
              className="w-full h-full object-contain bg-black"
              muted
              playsInline
              onLoadedMetadata={i === 0 ? (e) => setDuration(e.target.duration || 0) : undefined}
              onTimeUpdate={
                i === 0
                  ? (e) => {
                      const d = e.target.duration || duration || 1;
                      setProgress((e.target.currentTime || 0) / d);
                    }
                  : undefined
              }
              onEnded={i === 0 ? () => setPlaying(false) : undefined}
            />
            {showOverlay && v.overlay && v.overlay.available && <VideoOverlay overlay={v.overlay} />}
            <span className="absolute top-2 left-2 font-mono-tel text-[9px] tracking-widest uppercase px-2 py-0.5 rounded bg-black/70 border border-cyan-500/40 text-cyan-300">
              {v.camera_angle}
            </span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 rounded-xl border border-slate-800 bg-[#12181F] px-3 py-2">
        <button data-testid="sync-play-btn" onClick={toggle} className="h-9 w-9 rounded-lg bg-emerald-500 text-[#0B0F12] flex items-center justify-center hover:bg-emerald-400 transition-colors">
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </button>
        <button data-testid="sync-step-back-btn" onClick={() => stepBy(-FRAME)} title="Previous frame" className="h-9 w-9 rounded-lg border border-slate-700 text-slate-300 flex items-center justify-center hover:border-slate-600 transition-colors">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button data-testid="sync-step-fwd-btn" onClick={() => stepBy(FRAME)} title="Next frame" className="h-9 w-9 rounded-lg border border-slate-700 text-slate-300 flex items-center justify-center hover:border-slate-600 transition-colors">
          <ChevronRight className="h-4 w-4" />
        </button>
        <button data-testid="sync-restart-btn" onClick={restart} className="h-9 w-9 rounded-lg border border-slate-700 text-slate-300 flex items-center justify-center hover:border-slate-600 transition-colors">
          <RotateCcw className="h-4 w-4" />
        </button>
        {anyOverlay && (
          <button
            data-testid="sync-overlay-toggle-btn"
            onClick={() => setShowOverlay((v) => !v)}
            title="Toggle AI stance lines"
            className={`h-9 px-2.5 rounded-lg border flex items-center gap-1.5 text-xs font-semibold transition-colors ${
              showOverlay ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300" : "border-slate-700 text-slate-300 hover:border-slate-600"
            }`}
          >
            {showOverlay ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        )}
        <input
          data-testid="sync-seek"
          type="range"
          min={0}
          max={1000}
          value={Math.round(progress * 1000)}
          onChange={(e) => seek(Number(e.target.value) / 1000)}
          className="flex-1 accent-emerald-500 cursor-pointer"
        />
        <span data-testid="sync-frame-time" className="font-mono-tel text-[10px] text-slate-400 w-10 text-right">
          {duration ? `${(progress * duration).toFixed(2)}s` : "0.00s"}
        </span>
      </div>
      <p className="font-mono-tel text-[10px] text-slate-500 text-center">
        All angles locked to the same frame · use ◀ ▶ to compare stance frame-by-frame
      </p>
    </div>
  );
};

export const PerformanceReport = ({ assessment }) => {
  const { report } = assessment;
  const tier = scoreTier(assessment.overall_score);
  const videos = assessment.videos && assessment.videos.length ? assessment.videos : null;
  const [angleIdx, setAngleIdx] = useState(0);
  const [sideBySide, setSideBySide] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const activeVid = angleIdx < (videos?.length || 0) ? angleIdx : 0;
  const overlay = (videos && videos[activeVid] && videos[activeVid].overlay) || report.overlay;
  const hasOverlay = overlay && overlay.available;
  const multiAngle = videos && videos.length > 1;
  const radarData = (report.pillars || []).map((p) => ({
    subject: p.title.split(" ")[0] + (p.title.split(" ")[1] ? "\n" + p.title.split(" ")[1] : ""),
    short: p.title.split("&")[0].trim(),
    value: p.score,
  }));

  return (
    <div data-testid="performance-report-dashboard" className="flex flex-col gap-6 animate-fade-up">
      {/* Action bar */}
      <div className="flex items-center justify-end gap-2">
        <button
          data-testid="share-link-btn"
          onClick={() => {
            const url = `${window.location.origin}/?report=${assessment.id}`;
            navigator.clipboard?.writeText(url).then(
              () => toast.success("Share link copied", { description: "Anyone with the link can view this report." }),
              () => toast.error("Couldn't copy link", { description: url })
            );
          }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/70 border border-slate-700 text-sm font-semibold text-slate-100 hover:bg-slate-700 transition-colors"
        >
          <Link2 className="h-4 w-4 text-cyan-400" /> Copy share link
        </button>
        <button
          data-testid="export-pdf-btn"
          onClick={() => generateReportPdf(assessment)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/70 border border-slate-700 text-sm font-semibold text-slate-100 hover:bg-slate-700 transition-colors"
        >
          <Download className="h-4 w-4 text-emerald-400" /> Export PDF
        </button>
      </div>

      {/* Video + summary */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 flex flex-col gap-4">
          {multiAngle && (
            <div className="flex flex-wrap items-center gap-2">
              <div data-testid="report-angle-switcher" className="flex flex-wrap items-center gap-2">
                {!sideBySide && videos.map((v, i) => (
                  <button
                    key={i}
                    data-testid={`report-angle-tab-${i}`}
                    onClick={() => setAngleIdx(i)}
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors ${
                      i === activeVid
                        ? "bg-cyan-500/15 border-cyan-500/50 text-cyan-300"
                        : "bg-[#12181F] border-slate-700 text-slate-400 hover:border-slate-600"
                    }`}
                  >
                    {v.camera_angle}
                  </button>
                ))}
              </div>
              <button
                data-testid="toggle-side-by-side-btn"
                onClick={() => setSideBySide((v) => !v)}
                className={`ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors ${
                  sideBySide ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300" : "bg-[#12181F] border-slate-700 text-slate-400 hover:border-slate-600"
                }`}
              >
                <Columns2 className="h-3.5 w-3.5" /> {sideBySide ? "Single view" : "Side-by-side sync"}
              </button>
            </div>
          )}
          {multiAngle && sideBySide ? (
            <SyncPlayer assessmentId={assessment.id} videos={videos} showOverlay={showOverlay} setShowOverlay={setShowOverlay} />
          ) : (
            <>
              {hasOverlay && (
                <button
                  data-testid="toggle-overlay-btn"
                  onClick={() => setShowOverlay((v) => !v)}
                  className={`self-start flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors ${
                    showOverlay ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300" : "bg-[#12181F] border-slate-700 text-slate-400 hover:border-slate-600"
                  }`}
                >
                  {showOverlay ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  {showOverlay ? "Hide AI overlay" : "Show AI overlay"}
                </button>
              )}
              <div className="relative rounded-2xl overflow-hidden border border-slate-800 bg-black">
                <video
                  key={activeVid}
                  data-testid="batter-video-element"
                  src={mediaUrl(assessment.id, activeVid)}
                  className="w-full max-h-[320px] object-contain bg-black"
                  controls
                  playsInline
                />
                {showOverlay && <VideoOverlay overlay={overlay} />}
              </div>
              {hasOverlay && showOverlay && (
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {(overlay.lines || []).map((ln, i) => (
                    <span key={i} className="flex items-center gap-1.5 text-[11px] text-slate-400">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: ratingColor(ln.status) }} />
                      {ln.label}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
          <div className="flex flex-wrap gap-2">
            <span data-testid="video-camera-angle-badge" className="font-mono-tel text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-md bg-cyan-500/10 border border-cyan-500/30 text-cyan-300">
              {assessment.camera_angle}
            </span>
            <span className="font-mono-tel text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-md bg-slate-800/70 border border-slate-700 text-slate-300">
              {assessment.handedness}
            </span>
            {report.detected_shot_type && (
              <span className="font-mono-tel text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
                {report.detected_shot_type}
              </span>
            )}
          </div>
        </div>

        <div className="lg:col-span-7 rounded-2xl border border-slate-800 bg-[#12181F] p-6 flex flex-col sm:flex-row items-center gap-6">
          <div className="flex flex-col items-center gap-2">
            <ScoreRing score={assessment.overall_score} />
            <span
              data-testid="overall-score-badge"
              className="font-mono-tel text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full"
              style={{ color: tier.color, background: tier.bg, border: `1px solid ${tier.border}` }}
            >
              {tier.label}
            </span>
          </div>
          <div className="flex-1 text-center sm:text-left">
            <p className="font-mono-tel text-[10px] tracking-[0.2em] text-cyan-400 uppercase mb-1">
              Net Efficiency Score
            </p>
            <h3 className="font-heading font-bold text-xl text-slate-100 mb-2">{assessment.batter_name}</h3>
            <p className="text-sm text-slate-400 leading-relaxed">{report.summary}</p>
          </div>
        </div>
      </div>

      {/* Ball speed & distance */}
      <BallMetrics metrics={report.ball_metrics} />

      {/* Radar + pillar bars */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div data-testid="radar-chart-container" className="lg:col-span-5 rounded-2xl border border-slate-800 bg-[#12181F] p-4">
          <p className="font-mono-tel text-[10px] tracking-[0.2em] text-cyan-400 uppercase mb-2 px-2">
            Biomechanic Radar
          </p>
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={radarData} outerRadius="70%">
              <PolarGrid stroke="rgba(255,255,255,0.08)" />
              <PolarAngleAxis dataKey="short" tick={{ fill: "#94A3B8", fontSize: 11 }} />
              <Radar dataKey="value" stroke="#10B981" fill="#10B981" fillOpacity={0.35} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {(report.pillars || []).map((p) => (
            <PillarBar key={p.id} pillar={p} testId={PILLAR_IDS[p.id] || `score-${p.id}`} />
          ))}
        </div>
      </div>

      {/* Detailed metric breakdown */}
      <div data-testid="biomechanical-breakdown" className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(report.pillars || []).map((p) => (
          <div key={p.id} className="rounded-2xl border border-slate-800 bg-[#12181F] p-5">
            <div className="flex items-center gap-2 mb-3">
              <Target className="h-4 w-4 text-emerald-400" />
              <h4 className="font-heading font-semibold text-slate-100">{p.title}</h4>
            </div>
            <div className="flex flex-col gap-2.5">
              {(p.metrics || []).map((m, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span
                    className="mt-1 h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: ratingColor(m.rating) }}
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-slate-200">{m.label}</span>
                      <span
                        className="font-mono-tel text-[9px] uppercase tracking-wider"
                        style={{ color: ratingColor(m.rating) }}
                      >
                        {m.rating}
                      </span>
                    </div>
                    {m.note && <p className="text-xs text-slate-500 mt-0.5">{m.note}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Strengths / improvements */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div data-testid="report-strengths-list" className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.04] p-5">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            <h4 className="font-heading font-semibold text-emerald-300">Strengths</h4>
          </div>
          <ul className="flex flex-col gap-2">
            {(report.strengths || []).map((s, i) => (
              <li key={i} className="text-sm text-slate-300 flex gap-2">
                <span className="text-emerald-400 mt-0.5">▸</span> {s}
              </li>
            ))}
          </ul>
        </div>
        <div data-testid="report-improvements-list" className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            <h4 className="font-heading font-semibold text-amber-300">Areas to Improve</h4>
          </div>
          <ul className="flex flex-col gap-2">
            {(report.improvements || []).map((s, i) => (
              <li key={i} className="text-sm text-slate-300 flex gap-2">
                <span className="text-amber-400 mt-0.5">▸</span> {s}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Drills */}
      <div data-testid="report-coach-drills" className="rounded-2xl border border-slate-800 bg-[#12181F] p-5">
        <div className="flex items-center gap-2 mb-4">
          <Dumbbell className="h-4 w-4 text-cyan-400" />
          <h4 className="font-heading font-semibold text-slate-100">Coach's Recommended Drills</h4>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {(report.drills || []).map((d, i) => (
            <div key={i} className="rounded-xl border border-slate-800 bg-[#0B0F12] p-4">
              <div className="font-mono-tel text-[10px] text-cyan-400 mb-2">DRILL {String(i + 1).padStart(2, "0")}</div>
              <p className="font-semibold text-sm text-slate-100 mb-1">{d.title}</p>
              <p className="text-xs text-slate-400 leading-relaxed">{d.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
